import { useEffect, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

// project imports
import NavItem from "../NavItem";

// assets
import { IconChevronDown } from "@tabler/icons";

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

// Same match rule as `NavItem`'s own `isSelected`: exact, or a path segment
// under the item's URL. Used to auto-open a group on a direct link or reload,
// not only when its header is clicked.
function hasActiveDescendant(menu, pathname) {
  return (menu.children || []).some((item) => {
    if (!item) return false;
    if (item.type === "collapse") return hasActiveDescendant(item, pathname);
    if (!item.url) return false;
    return pathname === item.url || pathname.startsWith(`${item.url}/`);
  });
}

/**
 * A navigation row that opens onto children.
 *
 * The open state used to paint its label `primary.contrastText` - white, on a
 * transparent row - so expanding a group made its own title disappear. The row
 * follows the same three states as a leaf item now, and its children are
 * indented against a guide line rather than floated at 90% width.
 */
const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Mount only: reflects where the page landed, not the live menu - a
    // rebuild from something like the contact-us badge must not re-open a
    // group the admin already collapsed.
    if (hasActiveDescendant(menu, window.location.pathname)) {
      setSelected(menu.id);
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);
  };

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return (
          <NavCollapse
            key={item.id}
            menu={item}
            level={level + 1}
          />
        );
      case "item":
        return (
          <NavItem
            key={item.id}
            item={item}
            level={level + 1}
          />
        );
      default:
        return (
          <Typography
            key={item.id}
            variant="h6"
            color="error"
            align="center"
          >
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;

  return (
    <>
      <ListItemButton
        sx={{
          borderRadius: 2,
          mb: 0.25,
          minHeight: 38,
          py: 0.75,
          pl: 1.5,
          pr: 1.25,
          alignItems: "center",
          color: open ? "text.primary" : "text.secondary",
          "&:hover": { backgroundColor: theme.palette.action.hover, color: "text.primary" },
          "&.Mui-selected": {
            backgroundColor: "transparent",
            color: "text.primary",
            "&:hover": { backgroundColor: theme.palette.action.hover },
          },
        }}
        selected={selected === menu.id}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}>
          <Icon
            stroke={1.7}
            size="1.15rem"
            color="currentColor"
          />
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography
              color="inherit"
              sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
            >
              {menu.title}
            </Typography>
          }
        />
        <IconChevronDown
          stroke={2}
          size="1rem"
          style={{
            color: "currentColor",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s ease",
          }}
        />
      </ListItemButton>

      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
          sx={{
            ml: 2.25,
            pl: 1,
            borderLeft: `1px solid ${theme.palette.divider}`,
          }}
        >
          {menus}
        </List>
      </Collapse>
    </>
  );
};

export default NavCollapse;
